/**
 * Análisis de entregas — vista ANALÍTICA (no de seguimiento).
 *
 * Enmarca las entregas como un dashboard de análisis espacial: dónde se
 * concentran, qué tan concentradas están (Pareto / top-N), cómo se distribuyen
 * por comuna, y KPIs de cobertura. El mapa (DeliveryDensityMap, heatmap de
 * densidad GPU con las coordenadas crudas, todas las regiones) es UN elemento
 * más, no el protagonista.
 *
 * Fuente: getDeliveryCoords(dateFrom, dateTo) → solo coordenadas {lat,lng} de
 * entregas (el endpoint no trae fecha/cliente, así que el análisis es espacial).
 * El mapa pinta las coords crudas apenas responde el endpoint; la agregación por
 * comuna (point-in-polygon indexado, geoUtils) alimenta SOLO los KPIs/Pareto/
 * ranking y se acota a la región seleccionada, que es de la que se cargan las
 * geocercas.
 *
 * Multi-región (F2f del plan de expansión a Argentina): las geocercas salen de
 * `utils/geofences` según el país del operador (`useCountry`). La región se elige
 * en el header y se persiste en la URL (?region=), igual que ?comunas.
 */
import { useState, useEffect, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
  ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Cell,
} from "recharts";
import { MapPin, Layers, Crosshair, Gauge, Trophy, RefreshCw, Inbox } from "lucide-react";
import dayjs from "dayjs";
import useTheme from "../../../hooks/useTheme";
import useAuth from "../../../hooks/useAuth";
import { useIsMobile } from "../../../hooks/useIsMobile";
import useOperatorCoverage from "../../../hooks/useOperatorCoverage";
import { useCountry } from "../../../context/CountryContext";
import DeliveryDensityMap from "../../../components/deliveryDensityMap/DeliveryDensityMap";
import DateRangeFilter, { rangeFromPreset } from "../../../components/dateRangeFilter/DateRangeFilter";
import { Card } from "../../../components/ui/Card";
import { PageHeader } from "../../../components/ui/PageHeader";
import { Skeleton } from "../../../components/ui/Skeleton";
import { EmptyState } from "../../../components/ui/EmptyState";
import { getDeliveryCoords } from "../../../api/packages";
import { getRegions as getGeoRegions } from "../../../api/settings";
import { countDeliveriesByCommune, interpolateColor } from "../../../utils/geoUtils";
import {
  getRegionsForCountry, loadRegionGeojson, resolveDefaultRegion, shortRegionName,
} from "../../../utils/geofences";
import { numberFormatterFor } from "../../../utils/formatters";
import { useUrlState, useUrlStateBatch } from "../../../hooks/useUrlState";
import { cn } from "../../../utils/cn";

const TOP_N = 12;

const PRESETS = [
  { key: "7d", quick: true },
  { key: "30d", quick: true },
  { key: "thisMonth", quick: true },
  { key: "lastMonth", quick: false },
  { key: "90d", quick: false },
];

function Kpi({ icon: Icon, tone = "brand", label, value, sub, onClick, active }) {
  const toneCls = {
    brand: "text-brand-700 bg-brand-50",
    success: "text-status-success bg-status-success-soft",
    warning: "text-status-warning bg-status-warning-soft",
    info: "text-status-info bg-status-info-soft",
    neutral: "text-fg-muted bg-bg-muted",
  }[tone] || "text-brand-700 bg-brand-50";
  const interactive = typeof onClick === "function";
  return (
    <Card
      padding="md"
      className={cn(
        "flex items-center gap-3",
        interactive && "cursor-pointer transition-colors hover:border-brand-300",
        active && "border-brand-500 bg-brand-50",
      )}
      onClick={interactive ? onClick : undefined}
      role={interactive ? "button" : undefined}
      tabIndex={interactive ? 0 : undefined}
      aria-pressed={interactive ? !!active : undefined}
      onKeyDown={
        interactive
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
    >
      <span className={cn("w-10 h-10 rounded-lg flex items-center justify-center shrink-0", toneCls)}>
        <Icon className="w-5 h-5" />
      </span>
      <div className="min-w-0">
        <div className="text-xl font-semibold text-fg leading-none">{value}</div>
        <div className="text-xs text-fg-muted mt-1">{label}</div>
        {sub && <div className="text-[11px] text-fg-subtle mt-0.5">{sub}</div>}
      </div>
    </Card>
  );
}

// Lee un triplete RGB de un token del design-system y lo formatea concreto para
// recharts (que pinta atributos SVG, donde var() no resuelve). Fallback SSR.
const tokenRGB = (name, fallback) => {
  if (typeof window === "undefined" || !window.getComputedStyle) return fallback;
  const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return v ? `rgb(${v.replace(/\s+/g, ", ")})` : fallback;
};

export default function DeliveryAnalytics() {
  const { t } = useTranslation("tracking");
  const { isDark } = useTheme();
  const isMobile = useIsMobile();
  const { countryId, countryCode, locale, loading: countryLoading } = useCountry();
  const auth = useAuth();
  // Domicilio operativo principal del operador: es la mejor pista disponible
  // sobre en qué región trabaja. `state` es texto libre del formulario de
  // /admin/settings?tab=locations, así que el matcheo del manifest es tolerante.
  const operatorState = auth?.user?.logisticCompany?.operationsAddresses?.[0]?.state ?? "";

  // Formateador de miles según el país del operador (es-CL "12.345",
  // es-AR "12.345"): sustituye al formatNumberCL fijo de la vista.
  const fmtNum = useMemo(() => numberFormatterFor(locale), [locale]);

  // Preset persistido en la URL (?preset=). Default "30d" ⇒ sin param la vista se
  // comporta IDÉNTICO a antes (URL limpia). Permite deep-link/bookmark del rango.
  const [preset] = useUrlState("preset", "30d");
  // El rango 'custom' no es reconstruible desde el preset solo: lo persistimos
  // aparte en ?from/?to para poder rehidratarlo en reload/back.
  const [fromUrl] = useUrlState("from", "");
  const [toUrl] = useUrlState("to", "");
  // Setter atómico para cambiar preset + from/to en UNA sola navegación (en
  // react-router v6 dos setSearchParams encadenados en el mismo handler se pisan).
  const setDateParams = useUrlStateBatch({
    preset: { defaultValue: "30d" },
    from: { defaultValue: "" },
    to: { defaultValue: "" },
  });
  // rangeFromPreset devuelve un ARRAY [startMoment, endMoment]; lo normalizamos
  // a strings "YYYY-MM-DD" (ISO 8601 válido) igual que onChange de DateRangeFilter.
  // Sin esto, range.start/end quedaban undefined en el primer render y el back
  // rechazaba "dateFrom/dateTo must be a valid ISO 8601 date string". Sembramos
  // SÍNCRONO desde la URL: si el preset es 'custom' usamos ?from/?to, si no
  // derivamos la ventana del preset, para que el primer fetch ya use su rango.
  const initial = useMemo(() => {
    if (preset === "custom" && fromUrl && toUrl) return { start: fromUrl, end: toUrl };
    const [s, e] = rangeFromPreset(preset === "custom" ? "30d" : preset);
    return { start: s.format("YYYY-MM-DD"), end: e.format("YYYY-MM-DD") };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [range, setRange] = useState({ start: initial.start, end: initial.end });
  // geojson de comunas: undefined = cargando, null = falló el fetch, objeto = listo.
  const [geojson, setGeojson] = useState(undefined);
  const [coords, setCoords] = useState([]); // [{ latitude, longitude }] crudos del endpoint
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  // Comunas resaltadas persistidas en la URL (?comunas=csv). Default [] ⇒ omitido.
  const [selectedComunas, setSelectedComunas] = useUrlState("comunas", [], { type: "csv" });
  // Región seleccionada, persistida en la URL (?region=). Default "" ⇒ omitido,
  // y la región efectiva la decide `resolveDefaultRegion`.
  const [regionParam] = useUrlState("region", "");
  // Región y comunas se cambian JUNTAS (al cambiar de región las comunas
  // resaltadas de la anterior dejan de existir): dos setSearchParams encadenados
  // en el mismo handler se pisan en react-router v6.
  const setRegionParams = useUrlStateBatch({
    region: { defaultValue: "" },
    comunas: { type: "csv", defaultValue: [] },
  });

  // ── Catálogo de regiones del país del operador (manifest de geocercas) ──
  // undefined = cargando · [] = el país no tiene datasets generados.
  const [allRegions, setAllRegions] = useState(undefined);
  // Fuerza un re-fetch de la geocerca tras un fallo (el botón Actualizar).
  const [geoNonce, setGeoNonce] = useState(0);

  useEffect(() => {
    if (countryLoading) return undefined;
    let alive = true;
    setAllRegions(undefined);
    getRegionsForCountry(countryCode)
      .then((list) => { if (alive) setAllRegions(list); })
      .catch((err) => {
        if (!alive) return;
        console.warn("DeliveryAnalytics: no se pudo leer el manifest de geocercas", err);
        setAllRegions([]);
      });
    return () => { alive = false; };
  }, [countryCode, countryLoading, geoNonce]);

  // ── Acotar el selector a la cobertura declarada del operador ──
  // `coverageAreas` viene con los ObjectId de `countries.regions[]._id`, que solo
  // expone el módulo `geo`; el manifest, en cambio, indexa por el `code` de la
  // región (`dbRegionCode`). El puente es la lista de `geo`, que trae ambos.
  const { coverageAreas } = useOperatorCoverage();
  const [geoRegions, setGeoRegions] = useState(null);

  useEffect(() => {
    if (countryLoading || !countryId) return undefined;
    let alive = true;
    getGeoRegions(countryId)
      .then((res) => { if (alive) setGeoRegions(Array.isArray(res?.data) ? res.data : []); })
      .catch((err) => {
        if (!alive) return;
        console.warn("DeliveryAnalytics: no se pudo leer el catálogo geo para cruzar la cobertura", err);
        setGeoRegions([]);
      });
    return () => { alive = false; };
  }, [countryId, countryLoading]);

  // `null` ⇒ no filtrar. Se devuelve null tanto si la cobertura aún no se conoce
  // como si el operador no declaró ninguna: esta vista es de solo lectura, y
  // dejar el selector vacío la inutilizaría. Solo se filtra cuando hay una
  // cobertura real que cruzar.
  const coveredRegionCodes = useMemo(() => {
    if (!Array.isArray(coverageAreas) || !coverageAreas.length) return null;
    if (!Array.isArray(geoRegions) || !geoRegions.length) return null;
    const codeById = new Map(geoRegions.map((r) => [String(r._id), String(r.code)]));
    const codes = new Set();
    for (const area of coverageAreas) {
      const code = codeById.get(String(area?.regionId));
      if (code) codes.add(code);
    }
    return codes.size ? codes : null;
  }, [coverageAreas, geoRegions]);

  // Si el cruce deja el selector vacío (cobertura de otro país, códigos que no
  // calzan con el manifest) se muestra el catálogo completo: es preferible a una
  // vista muerta, y el aviso de "entregas fuera de la región" ya explica el resto.
  const regions = useMemo(() => {
    if (!Array.isArray(allRegions) || !coveredRegionCodes) return allRegions;
    const filtered = allRegions.filter((r) => coveredRegionCodes.has(String(r.dbRegionCode)));
    return filtered.length ? filtered : allRegions;
  }, [allRegions, coveredRegionCodes]);

  const defaultRegionCode = useMemo(
    () => resolveDefaultRegion(regions ?? [], { countryCode, stateName: operatorState }),
    [regions, countryCode, operatorState],
  );

  // La región del querystring manda, pero solo si existe en el manifest del país
  // actual: un ?region= copiado de otro operador (u otro país) no debe dejar la
  // vista pidiendo un archivo inexistente.
  const regionCode = useMemo(() => {
    if (!regions?.length) return "";
    if (regionParam && regions.some((r) => String(r.regionCode) === String(regionParam))) {
      return String(regionParam);
    }
    return defaultRegionCode;
  }, [regions, regionParam, defaultRegionCode]);

  const region = useMemo(
    () => (regions ?? []).find((r) => String(r.regionCode) === String(regionCode)) ?? null,
    [regions, regionCode],
  );
  // Nombre corto para el texto corrido ("Metropolitana de Santiago", "Mendoza").
  const regionLabel = region ? shortRegionName(region.name) : "";

  useEffect(() => {
    if (!regionCode) return undefined;
    let alive = true;
    setGeojson(undefined);
    loadRegionGeojson(countryCode, regionCode)
      .then((data) => { if (alive) setGeojson(data); })
      .catch((err) => {
        if (!alive) return;
        console.warn("DeliveryAnalytics: no se pudo cargar la geocerca de la región", err);
        setGeojson(null);
      });
    return () => { alive = false; };
  }, [countryCode, regionCode, geoNonce]);

  const handleRegionChange = useCallback(
    (code) => setRegionParams({ region: code, comunas: [] }),
    [setRegionParams],
  );

  // El fetch de coords ya NO depende del geojson: el heatmap pinta las coords
  // crudas apenas responde el endpoint. El geojson solo se necesita para el
  // desglose por comuna (derivado más abajo).
  const load = useCallback(async (from, to, manual) => {
    manual ? setRefreshing(true) : setLoading(true);
    setError(null);
    try {
      const res = await getDeliveryCoords(from, to);
      if (res.success && res.data?.data) {
        setCoords(res.data.data);
        setTotal(res.data.total ?? res.data.data.length);
      } else {
        setError(res.error?.message || t("No se pudieron cargar las entregas."));
        setCoords([]); setTotal(0);
      }
    } catch (e) {
      // Igual que el original: ante excepción conservamos los datos previos en
      // pantalla (solo la respuesta no-exitosa limpia); el banner de error avisa.
      setError(t("No se pudieron cargar las entregas."));
    } finally {
      setLoading(false); setRefreshing(false);
    }
  }, [t]);

  useEffect(() => { load(range.start, range.end, false); }, [range, load]);

  // Conteo por comuna (KPIs/Pareto/ranking, solo la región seleccionada). El
  // índice espacial de geoUtils lo deja en decenas de ms incluso en Magallanes
  // (121k vértices), pero sigue siendo O(N): useMemo para no repetirlo por render.
  const counted = useMemo(
    () => (geojson && coords.length ? countDeliveriesByCommune(coords, geojson) : []),
    [geojson, coords]
  );

  const handleRange = (start, end, p) => {
    setRange({ start, end });
    if (p === "custom") {
      // Custom: persistimos preset + fechas concretas para reconstruir en reload/back.
      setDateParams({ preset: "custom", from: start, to: end });
    } else if (p) {
      // Volver a un preset limpia ?from/?to.
      setDateParams({ preset: p, from: "", to: "" });
    }
  };

  // Re-deriva el rango cuando cambia el preset (selección / back-forward) o, para
  // 'custom', cuando cambian ?from/?to. Solo actualiza el rango; la carga sigue
  // disparándose por el efecto de [range, load]. El guard de contenido
  // evita re-render y re-fetch redundantes cuando el valor derivado no cambió.
  useEffect(() => {
    let next;
    if (preset === "custom") {
      if (!fromUrl || !toUrl) return;
      next = { start: fromUrl, end: toUrl };
    } else {
      const [s, e] = rangeFromPreset(preset);
      next = { start: s.format("YYYY-MM-DD"), end: e.format("YYYY-MM-DD") };
    }
    setRange((prev) => (prev.start === next.start && prev.end === next.end ? prev : next));
  }, [preset, fromUrl, toUrl]);

  // ── Derivados de análisis ──
  const ranked = useMemo(() => [...counted].filter((d) => d.count > 0).sort((a, b) => b.count - a.count), [counted]);
  const totalCounted = useMemo(() => ranked.reduce((a, d) => a + d.count, 0), [ranked]);
  const comunasConEntregas = ranked.length;

  // Pareto: top-N con % individual y acumulado. En mobile menos comunas:
  // 12 labels rotados a -40° no caben legibles en 390px.
  const topN = isMobile ? 6 : TOP_N;
  const pareto = useMemo(() => {
    let acc = 0;
    return ranked.slice(0, topN).map((d) => {
      acc += d.count;
      return {
        comuna: d.comuna,
        count: d.count,
        pct: totalCounted ? (d.count / totalCounted) * 100 : 0,
        acumPct: totalCounted ? (acc / totalCounted) * 100 : 0,
      };
    });
  }, [ranked, totalCounted, topN]);

  // Concentración: cuántas comunas acumulan el 80% de las entregas.
  const comunas80 = useMemo(() => {
    let acc = 0; let n = 0;
    for (const d of ranked) { acc += d.count; n += 1; if (totalCounted && acc / totalCounted >= 0.8) break; }
    return n;
  }, [ranked, totalCounted]);

  const top5share = useMemo(() => {
    const top5 = ranked.slice(0, 5).reduce((a, d) => a + d.count, 0);
    return totalCounted ? Math.round((top5 / totalCounted) * 100) : 0;
  }, [ranked, totalCounted]);

  const densidadMedia = comunasConEntregas ? Math.round(totalCounted / comunasConEntregas) : 0;
  const lider = ranked[0];
  const maxCount = lider?.count || 1;

  const rangeLabel = `${dayjs(range.start).format("DD/MM/YYYY")} – ${dayjs(range.end).format("DD/MM/YYYY")}`;
  // Colores de grilla/ticks/línea derivados de los tokens del design-system
  // (auto dark mode: --border y --fg-muted conmutan con .dark). isDark fuerza
  // el recálculo al cambiar de tema.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const grid = useMemo(() => tokenRGB("--border", "#E3E3E3"), [isDark]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const tick = useMemo(() => tokenRGB("--fg-muted", "#666666"), [isDark]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const accentLine = useMemo(() => tokenRGB(isDark ? "--brand-400" : "--brand-500", "#F97316"), [isDark]);
  const onComunaSelect = useCallback((c) => {
    setSelectedComunas((prev) => prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c]);
  }, [setSelectedComunas]);

  // El país no tiene geocercas generadas: no hay región que cargar, así que no
  // se puede quedar "cargando" para siempre.
  const noRegions = Array.isArray(regions) && regions.length === 0;
  const geoLoading = !noRegions && geojson === undefined;
  const geoFailed = noRegions || geojson === null;
  // Los paneles por comuna dependen de coords + geojson: su "cargando" cubre
  // ambos fetches para no mostrar "Sin entregas"/ceros mientras falta el geojson
  // (los fetches ahora corren en paralelo y coords puede llegar primero).
  const communeLoading = loading || geoLoading;
  const hasData = !communeLoading && totalCounted > 0;
  // El KPI "Entregas" (total) cuenta TODAS las entregas del período (lo que trae el
  // backend), pero el análisis por comuna (mapa/Pareto/ranking) solo cuenta las que
  // caen dentro de un polígono de la región seleccionada. La diferencia son
  // entregas de otras regiones que el desglose no muestra: la hacemos explícita.
  const totalAll = total || totalCounted;
  const fueraRegion = Math.max(0, totalAll - totalCounted);

  return (
    <div className="w-full min-w-0 p-4 sm:p-6 space-y-5">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <PageHeader
          icon={<MapPin className="w-6 h-6 text-fg-muted" />}
          title={t("Análisis de entregas")}
          subtitle={t("Dónde y cuánto se concentran las entregas. Análisis espacial por comuna del período.")}
        />
        {/* items-start: DateRangeFilter es más alto que los demás controles
            (bajo los chips cuelga la leyenda del rango). Con items-center el
            selector de región y el botón de refrescar quedaban descolgados
            respecto de la fila de chips. */}
        <div className="flex flex-wrap items-start gap-2">
          {regions?.length > 0 && (
            <select
              value={regionCode}
              onChange={(e) => handleRegionChange(e.target.value)}
              aria-label={t("Región del análisis")}
              title={t("Región del análisis")}
              className="h-9 max-w-[16rem] rounded-lg border border-border bg-bg px-2 text-sm text-fg hover:border-brand-300 focus:outline-none focus:ring-2 focus:ring-brand-500/40"
            >
              {regions.map((r) => (
                <option key={r.regionCode} value={r.regionCode}>{r.name}</option>
              ))}
            </select>
          )}
          <DateRangeFilter value={preset} customRange={preset === "custom" ? range : null} onChange={handleRange} presets={PRESETS} />
          <button type="button" className="h-9 w-9 inline-flex items-center justify-center rounded-lg border border-border text-fg-muted hover:text-brand-700 hover:border-brand-300 disabled:opacity-50"
            onClick={() => { if (geojson === null || noRegions) setGeoNonce((n) => n + 1); load(range.start, range.end, true); }} disabled={loading || refreshing} aria-label={t("Actualizar")}>
            <RefreshCw className={cn("w-4 h-4", refreshing && "animate-spin")} />
          </button>
        </div>
      </div>

      {error && (
        <div className="text-sm text-status-error bg-status-error-soft rounded-lg px-3 py-2">{error}</div>
      )}

      {geoFailed && (
        <div className="text-sm text-status-warning bg-status-warning-soft rounded-lg px-3 py-2">
          {noRegions
            ? t("No hay geocercas disponibles para el país de operación; el desglose por comuna no está disponible.")
            : t("No se pudo cargar el mapa de comunas; el desglose por comuna no está disponible. Presiona Actualizar para reintentar.")}
        </div>
      )}

      {/* KPIs de análisis */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
        <Kpi icon={MapPin} tone="brand" label={t("Entregas")} value={loading ? "—" : fmtNum(total || totalCounted)} sub={rangeLabel} />
        <Kpi icon={Layers} tone="info" label={t("Comunas con entregas")} value={communeLoading ? "—" : comunasConEntregas} />
        <Kpi icon={Crosshair} tone="warning" label={t("Concentración top 5")} value={communeLoading ? "—" : `${top5share}%`} sub={t("del total")} />
        <Kpi icon={Gauge} tone="neutral" label={t("Comunas = 80%")} value={communeLoading ? "—" : comunas80} sub={t("acumulan 80% entregas")} />
        <Kpi
          icon={Trophy}
          tone="success"
          label={t("Comuna líder")}
          value={communeLoading ? "—" : (lider?.comuna || "—")}
          sub={lider ? `${fmtNum(lider.count)} ${t("entregas")}` : ""}
          onClick={lider ? () => onComunaSelect(lider.comuna) : undefined}
          active={lider ? selectedComunas.includes(lider.comuna) : false}
        />
      </div>

      {/* Alcance explícito: el desglose por comuna cubre solo la región
          seleccionada. El KPI "Entregas" cuenta el total del período; aquí
          avisamos cuántas quedan fuera de esa región. */}
      {hasData && (
        <div className="flex items-start gap-2 rounded-lg border border-border bg-bg-muted/40 px-3 py-2 text-xs text-fg-muted">
          <MapPin className="w-3.5 h-3.5 mt-0.5 shrink-0 text-fg-subtle" />
          <p className="min-w-0">
            {t("El análisis por comuna (Pareto y ranking) considera solo")} {regionLabel};{" "}
            {t("el mapa muestra todas las entregas.")}
            {fueraRegion > 0 && (
              <>
                {" "}
                <span className="font-medium text-fg">{fmtNum(fueraRegion)} {t("entregas fuera de")} {regionLabel}</span>
                {" "}
                {t("no se incluyen en el desglose")} ({fmtNum(totalAll)} {t("en total")} · {fmtNum(totalCounted)} {t("en")} {regionLabel}).
              </>
            )}
          </p>
        </div>
      )}

      {/* Mapa (heatmap de densidad) + Pareto */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <Card padding="none" className="overflow-hidden">
          <div className="px-4 py-3 border-b border-border">
            <h3 className="text-sm font-semibold text-fg">{t("Mapa de calor de entregas")}</h3>
            <p className="text-xs text-fg-muted">{t("Densidad de entregas del período. Incluye todas las regiones.")}</p>
          </div>
          <div className="relative" style={{ height: 460 }}>
            <DeliveryDensityMap points={coords} geojson={geojson || null} />
            {loading && (
              <div className="absolute inset-0 z-20"><Skeleton className="w-full h-full" /></div>
            )}
            {!loading && !error && coords.length === 0 && (
              <div className="absolute inset-0 z-20 flex items-center justify-center bg-bg/70">
                <EmptyState icon={<Inbox />} title={t("Sin entregas en el período")} />
              </div>
            )}
          </div>
        </Card>

        <Card padding="md" className="flex flex-col">
          <h3 className="text-sm font-semibold text-fg">{t("Pareto de concentración")} <span className="ml-1 align-middle text-[10px] font-medium text-fg-subtle">{t("solo")} {regionLabel}</span></h3>
          <p className="text-xs text-fg-muted mb-3">{t("Top comunas + % acumulado del total de entregas en")} {regionLabel}.</p>
          {communeLoading ? (
            <Skeleton className="h-72 rounded-lg" />
          ) : !hasData ? (
            <EmptyState icon={<Inbox />} title={geoFailed ? t("Desglose por comuna no disponible") : t("Sin entregas en el período")} />
          ) : (
            <ResponsiveContainer width="100%" height={360}>
              <ComposedChart data={pareto} margin={{ top: 8, right: 8, left: -10, bottom: 40 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={grid} vertical={false} />
                <XAxis dataKey="comuna" tick={{ fill: tick, fontSize: 10 }} angle={-40} textAnchor="end" interval={0} height={60} />
                <YAxis yAxisId="l" tick={{ fill: tick, fontSize: 11 }} />
                <YAxis yAxisId="r" orientation="right" domain={[0, 100]} tickFormatter={(v) => `${v}%`} tick={{ fill: tick, fontSize: 11 }} />
                <Tooltip
                  contentStyle={{ background: "rgb(var(--bg))", border: "1px solid rgb(var(--border))", borderRadius: 8, fontSize: 12 }}
                  formatter={(val, name) => name === "acumPct" ? [`${val.toFixed(1)}%`, t("Acumulado")] : [fmtNum(val), t("Entregas")]}
                />
                <Bar yAxisId="l" dataKey="count" radius={[4, 4, 0, 0]}>
                  {pareto.map((d, i) => {
                    const [r, g, b] = interpolateColor(d.count / maxCount);
                    return <Cell key={i} fill={`rgb(${r},${g},${b})`} />;
                  })}
                </Bar>
                <Line yAxisId="r" type="monotone" dataKey="acumPct" stroke={accentLine} strokeWidth={2.5} dot={{ r: 2.5 }} />
              </ComposedChart>
            </ResponsiveContainer>
          )}
        </Card>
      </div>

      {/* Ranking analítico con % y acumulado */}
      <Card padding="none" className="overflow-hidden">
        <div className="px-4 py-3 border-b border-border flex items-center justify-between">
          <h3 className="text-sm font-semibold text-fg">{t("Ranking de comunas")} <span className="ml-1 align-middle text-[10px] font-medium text-fg-subtle">{t("solo")} {regionLabel}</span></h3>
          <span className="text-xs text-fg-muted">{comunasConEntregas} {t("comunas")} · {fmtNum(totalCounted)} {t("entregas en")} {regionLabel}</span>
        </div>
        {communeLoading ? (
          <div className="p-4 space-y-2">{Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-9 rounded" />)}</div>
        ) : !hasData ? (
          <EmptyState icon={<Inbox />} title={geoFailed ? t("Desglose por comuna no disponible") : t("Sin entregas en el período")} />
        ) : isMobile ? (
          <div className="p-3 space-y-2">
            {ranked.map((d, i) => {
              const pct = totalCounted ? (d.count / totalCounted) * 100 : 0;
              const [r, g, b] = interpolateColor(d.count / maxCount);
              const sel = selectedComunas.includes(d.comuna);
              return (
                <button
                  type="button"
                  key={d.comuna}
                  onClick={() => onComunaSelect(d.comuna)}
                  className={cn(
                    "w-full text-left rounded-lg border p-3 transition-colors",
                    sel ? "border-brand-500 bg-brand-50" : "border-border bg-bg hover:bg-bg-muted/50",
                  )}
                >
                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center justify-center min-w-6 h-6 px-1.5 rounded-full bg-bg-muted text-[11px] font-bold text-fg-subtle tabular-nums">{i + 1}</span>
                    <span className="flex-1 min-w-0 truncate font-medium text-fg">{d.comuna}</span>
                    <span className="text-fg font-semibold tabular-nums">{fmtNum(d.count)}</span>
                  </div>
                  <div className="mt-2 flex items-center gap-2">
                    <div className="flex-1 h-2 rounded-full bg-bg-muted overflow-hidden">
                      <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: `rgb(${r},${g},${b})` }} />
                    </div>
                    <span className="text-xs text-fg-muted tabular-nums shrink-0">{pct.toFixed(1)}%</span>
                  </div>
                </button>
              );
            })}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-bg-muted">
                <tr>
                  <th className="px-4 py-2 text-left text-[11px] font-semibold text-fg-muted uppercase">#</th>
                  <th className="px-4 py-2 text-left text-[11px] font-semibold text-fg-muted uppercase">{t("Comuna")}</th>
                  <th className="px-4 py-2 text-right text-[11px] font-semibold text-fg-muted uppercase">{t("Entregas")}</th>
                  <th className="px-4 py-2 text-right text-[11px] font-semibold text-fg-muted uppercase">% {t("del total")}</th>
                  <th className="px-4 py-2 text-left text-[11px] font-semibold text-fg-muted uppercase w-40">{t("Participación")}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {(() => { let acc = 0; return ranked.map((d, i) => {
                  acc += d.count;
                  const pct = totalCounted ? (d.count / totalCounted) * 100 : 0;
                  const [r, g, b] = interpolateColor(d.count / maxCount);
                  const sel = selectedComunas.includes(d.comuna);
                  return (
                    <tr key={d.comuna} className={cn("cursor-pointer hover:bg-bg-muted/50", sel && "bg-brand-50")} onClick={() => onComunaSelect(d.comuna)}>
                      <td className="px-4 py-2 text-fg-subtle">{i + 1}</td>
                      <td className="px-4 py-2 font-medium text-fg whitespace-nowrap">{d.comuna}</td>
                      <td className="px-4 py-2 text-right text-fg tabular-nums">{fmtNum(d.count)}</td>
                      <td className="px-4 py-2 text-right text-fg-muted tabular-nums">{pct.toFixed(1)}%</td>
                      <td className="px-4 py-2">
                        <div className="h-2 rounded-full bg-bg-muted overflow-hidden">
                          <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: `rgb(${r},${g},${b})` }} />
                        </div>
                      </td>
                    </tr>
                  );
                }); })()}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
